import { ChartSettingsT, ExportFileT } from 'business/definitions';
import { convertToQueryFilters } from 'helpers/filterHelper';
import { downloadFile, values } from 'helpers/helpers';
import _ from 'lodash';
import { useMutation } from '@tanstack/react-query';
import { RQAPICall } from 'store/APIClient';
import { DEFAULT_EXPORTED_CHART_TITLE } from 'business/constants';
import { transformQueryToDataQuery } from 'helpers/queryHelpers';
import { camelize } from 'helpers/syntaxHelper';
import { useSettingsContext } from '../components/Contexts/SettingsContext/SettingsContext';
import { slugify } from 'helpers/formatHelper';
import { t } from 'i18next';

interface IExportFilePayload {
    fileType: ExportFileT;
    query: QueryT;
    title?: string;
    chartSettings?: ChartSettingsT;
    selectedViz?: string;
    columns?: string[];
}

const computeGetDataEndpoint = (
    chartQuery: QueryT,
    dashboardFilters: DashboardFilterT[],
): QueryT => {
    // @ts-expect-error number | sentenceFilterT[] here, should only return sentenceFilterT[]
    const extraFilters: SentenceFilterT[] = values(
        dashboardFilters,
        // @ts-expect-error [TS migration] Was not detected by flow
    ).filter((f) => f.value != null && f.value !== '');

    // dashboard filters (and navigation filter, that can override a dashboard filter's value)
    const cleanExtraFilters = convertToQueryFilters(extraFilters);

    // remove duplicates, the priority goes to the dashboard filter value over the chart filter's
    const chartFiltersWithoutExtraFilters = _.differenceBy(
        chartQuery.filters,
        cleanExtraFilters,
        'field',
    );

    return {
        ...chartQuery,
        filters: [...cleanExtraFilters, ...chartFiltersWithoutExtraFilters],
    };
};

export const getTitle = (
    title: string | undefined,
    contextChartTitle: string,
): string => {
    return (
        slugify(title ?? '') ||
        slugify(contextChartTitle) ||
        slugify(t(DEFAULT_EXPORTED_CHART_TITLE))
    );
};

/**
 * Extracts the visible dimensions ordered by the columnOrder if it exists, otherwise by the columnsWidth.
 */
const extractVisibleDimensions = (
    chartSettings?: ChartSettingsT,
): string[] | undefined => {
    if (!chartSettings) return undefined;

    const { columnOrder, columnsWidth, columnsHidden } = chartSettings;

    if (!columnsWidth && !columnOrder) return undefined;

    if (columnOrder)
        return columnOrder.filter(
            (col) => !columnsHidden || !columnsHidden.includes(col),
        );

    if (columnsWidth)
        return columnsWidth
            .filter(
                (col) =>
                    !columnsHidden || !columnsHidden.includes(col.columnName),
            )
            .map((col) => col.columnName);

    return undefined;
};

/**
 * Get the mapping between the columns names and the query fields
 * This method is useful when we can't get the field names directly from the columns by using snakify
 */
const getColumnsNamesToQueryMapping = (
    columns: string[] | undefined,
): Record<string, string> => {
    const result = {} as Record<string, string>;

    if (!columns) return result;

    columns.reduce((acc, col) => {
        acc[camelize(col)] = col;
        return acc;
    }, result);

    return result;
};

export const useExportToFile = (dashboardFilters: DashboardFilterT[]) => {
    const { chartTitle } = useSettingsContext();

    const mutation = useMutation({
        mutationFn: (payload: IExportFilePayload) => {
            let visibleDimensions =
                payload.chartSettings &&
                payload.columns &&
                payload.selectedViz &&
                payload.selectedViz === 'table'
                    ? extractVisibleDimensions(payload.chartSettings)
                    : undefined;

            if (visibleDimensions) {
                const mapping = getColumnsNamesToQueryMapping(payload.columns);
                visibleDimensions = visibleDimensions?.map((d) => mapping[d]);
            }
            const query = computeGetDataEndpoint(
                payload.query,
                dashboardFilters,
            );

            return RQAPICall<Blob>({
                endpoint: 'query/get_data/v2/download',
                method: 'POST',
                body: {
                    ...transformQueryToDataQuery(query),
                    download_format: payload.fileType,
                    visible_dimensions: visibleDimensions,
                },
            });
        },

        onSuccess: (response, payload) => {
            const title = getTitle(payload.title, chartTitle);
            const downloadURL = window.URL.createObjectURL(response.content);
            downloadFile(downloadURL, title, payload.fileType);
        },
    });
    return {
        exportToFile: mutation.mutate,
        isExporting: mutation.isPending,
    };
};

export const useExportToFileFromMetricsDimensionsAndFilters = (
    chartTitle: string,
    queryMetrics: QueryMetricT[],
    queryDimensions: QueryDimensionT[],
    queryFilters: QueryFilterT[],
    chartSettings: ChartSettingsT | undefined,
    columns?: string[],
    vizType?: string,
    dashboardFilters: DashboardFilterT[] = [],
) => {
    const { exportToFile, isExporting } = useExportToFile(dashboardFilters);
    const exportToFileFromMetricsDimensionsAndFilters = (
        fileType: ExportFileT,
    ) =>
        exportToFile({
            query: {
                metrics: queryMetrics,
                dimensions: queryDimensions,
                filters: queryFilters,
            },
            fileType,
            title: chartTitle,
            chartSettings,
            selectedViz: vizType,
            columns: columns || [
                ...queryDimensions.map((d) => d.field),
                ...queryMetrics.map((m) => m.field),
            ],
        });

    return {
        exportToFile: exportToFileFromMetricsDimensionsAndFilters,
        isExporting,
    };
};
